// A single bookable slot in an experience's calendar.
//
// Only 'direct' experiences have any (see `isCalendarBookingType`). An inquiry
// is worked the other way round — the partner offers dates and we go and ask the
// artisan — so a calendar there would be a promise nobody has made.
//
// A slot is one artisan for one span of one day, and it takes exactly one
// booking. There is no capacity field, deliberately: these are private
// experiences held for one party alone, and a "capacity: 1" that could be edited
// to 2 would be a way to double-book a person rather than a feature. Two parties
// on the same day means two slots, which is also the only way to say that they
// are at different times.
//
// Writes are split between the CMS and the backend, and the split is the whole
// safety property. An admin creates and deletes slots straight from the browser
// (firestore.rules lets them, so long as the slot is not booked), but nothing in
// a browser may set `status` to 'booked' — that happens inside the transaction in
// `createBookingRequest`, which is what makes "one slot, one booking" true when
// two concierges press the button at the same instant.

import { jstInstant } from './types_bookingRequest';
import { isDateClosed, type ExperienceClosuresType } from './types_closure';

// Firestore collection name.
export const coll_availabilitySlots = 'availabilitySlots';

/**
 * Whether a slot is still on offer.
 *
 * Two states rather than three: there is no 'held'. A hold is what an inquiry
 * needs, because somebody has to go and ask; a slot is availability the artisan
 * has already agreed to, so the concierge who takes it has it.
 */
export type AvailabilitySlotStatusType = 'open' | 'booked';

/** What the CMS form collects. Everything else is derived or server-set. */
export interface AvailabilitySlotDraftType {
  /** `yyyy-mm-dd`, the Japanese calendar date. */
  date: string;
  /** `HH:mm`, 24-hour, JST. */
  startTime: string;
  endTime: string;
}

export interface AvailabilitySlotType extends AvailabilitySlotDraftType {
  id: string;
  experienceId: string;

  status: AvailabilitySlotStatusType;

  /**
   * Which booking took it. Written with `status: 'booked'` in one transaction, so
   * a slot can never be closed without saying what closed it — and cleared again
   * if that booking is cancelled, which is what puts the date back on the
   * calendar.
   */
  bookingRequestId?: string;
  bookedAt?: string;

  createdAt: string;
  createdByUid?: string;
}

// `HH:mm`, 24-hour. Stricter than the browser's time input needs, because these
// strings are compared as text — "9:00" would sort after "10:00".
const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A stored slot with anything missing filled in from the defaults, for the same
 * reason `normaliseExperience` exists: a document written before a field was
 * added simply has no such key, and `undefined` is not something the Admin SDK
 * will write back.
 */
export function normaliseAvailabilitySlot(data: Partial<AvailabilitySlotType>, id: string): AvailabilitySlotType {
  return { ...makeEmptyAvailabilitySlot(), ...data, id };
}

export function makeEmptyAvailabilitySlot(): AvailabilitySlotType {
  return {
    id: '',
    experienceId: '',
    date: '',
    startTime: '',
    endTime: '',
    status: 'open',
    createdAt: '',
  };
}

/**
 * When a slot begins and ends, as instants.
 *
 * JST is UTC+9 all year, so this is exact arithmetic and needs no timezone
 * library — the same reasoning as `jstStartOfDay`, which this shares its
 * implementation with. Slots are quoted to partners in Japan in Japanese local
 * time; the functions comparing them to `now` run in UTC.
 */
export function slotStartInstant(slot: AvailabilitySlotDraftType): string {
  return jstInstant(slot.date, slot.startTime);
}

export function slotEndInstant(slot: AvailabilitySlotDraftType): string {
  return jstInstant(slot.date, slot.endTime);
}

/** "10:00–12:00". The times as the artisan and the partner both read them. */
export function slotTimeRangeLabel(slot: Pick<AvailabilitySlotDraftType, 'startTime' | 'endTime'>): string {
  if (!slot.startTime) return '';
  return slot.endTime ? `${slot.startTime}–${slot.endTime}` : slot.startTime;
}

/** Day, then time of day. The order a calendar is read in. */
export function compareSlots(a: AvailabilitySlotDraftType, b: AvailabilitySlotDraftType): number {
  return a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date);
}

/**
 * Whether a concierge may take this slot right now.
 *
 * The clock matters as much as the status: a slot whose start has passed is not
 * availability, whatever the document says, and nothing sweeps the collection to
 * mark yesterday's leftovers. Checked in the form and again inside the booking
 * transaction, so a page left open overnight is refused rather than honoured.
 */
export function isSlotBookable(slot: AvailabilitySlotType, now: string): boolean {
  return slot.status === 'open' && slotStartInstant(slot) > now;
}

/** Whether the slot's start has passed. Past slots are shown, never offered. */
export function isSlotPast(slot: AvailabilitySlotType, now: string): boolean {
  return slotStartInstant(slot) <= now;
}

/**
 * Whether two slots on the same day collide.
 *
 * Worth enforcing rather than trusting an admin to eyeball, because a slot is one
 * artisan: two overlapping slots are two partners booking the same person for the
 * same hour, and neither booking would look wrong on its own. Touching ends do
 * not overlap — 10:00–12:00 and 12:00–14:00 are two sittings, which is exactly
 * how a day gets filled.
 */
export function slotsOverlap(a: AvailabilitySlotDraftType, b: AvailabilitySlotDraftType): boolean {
  if (a.date !== b.date) return false;
  return a.startTime < b.endTime && b.startTime < a.endTime;
}

/**
 * Why a slot cannot be created, or null when it can.
 *
 * Shared by the CMS form and the security rules' intent in the same spirit as
 * `validateBookingRequest`: one definition of a well-formed slot, so the message
 * an admin reads while typing is the rule that actually applies. `existing` is
 * the slots already on this experience — pass them all; only the same day can
 * collide.
 */
export function validateAvailabilitySlot(
  draft: AvailabilitySlotDraftType,
  existing: AvailabilitySlotDraftType[],
): string | null {
  if (!isoDatePattern.test(draft.date)) return 'Please give the date as yyyy-mm-dd.';
  if (!timePattern.test(draft.startTime) || !timePattern.test(draft.endTime)) {
    return 'Please give both times as HH:mm, 24-hour.';
  }
  // Same-day only, which is why this is a plain string comparison. An experience
  // running past midnight would need two slots, and has not come up — a
  // late-evening session that ends at 23:59 is expressible.
  if (draft.endTime <= draft.startTime) return 'The end time must be after the start time.';
  if (existing.some((slot) => slotsOverlap(draft, slot))) {
    return 'That overlaps a slot already on this day.';
  }
  return null;
}

// --- Filling a run of days in one go ---
//
// Typing a month of an artisan's diary one row at a time is the job this exists
// to remove: most 'direct' experiences run the same handful of sittings every
// day they run at all, so the whole calendar is one date range and a list of
// times. It creates ordinary slots — nothing below is stored, and a slot made
// this way is indistinguishable from one typed by hand, which is what keeps
// deleting one an ordinary delete.

/** One sitting in the day, without the day. */
export interface SlotTimeRangeType {
  startTime: string;
  endTime: string;
}

/**
 * The sittings a day is filled with unless an admin says otherwise: six hours of
 * work with half an hour between each, 09:00 to 17:30.
 *
 * A starting point rather than a policy — the form lets any of them be changed,
 * removed or added to before anything is written.
 */
export const defaultDailySlotTimes: SlotTimeRangeType[] = [
  { startTime: '09:00', endTime: '10:00' },
  { startTime: '10:30', endTime: '11:30' },
  { startTime: '12:00', endTime: '13:00' },
  { startTime: '13:30', endTime: '14:30' },
  { startTime: '15:00', endTime: '16:00' },
  { startTime: '16:30', endTime: '17:30' },
];

/**
 * The longest run that can be filled at once.
 *
 * A year is more calendar than any artisan has agreed to, and the cap is really
 * there for the typo: a mistyped year in the second date is otherwise thousands
 * of documents to delete one at a time.
 */
export const bulkSlotMaxDays = 366;

/** How many days a run covers, both ends included. NaN for dates that cannot be read. */
export function calendarDayCount(startDate: string, endDate: string): number {
  const start = Date.parse(`${startDate}T12:00:00Z`);
  const end = Date.parse(`${endDate}T12:00:00Z`);
  // Noon-to-noon in UTC, so a day is exactly 86,400,000ms and daylight saving
  // never comes into it — the same reasoning as `isoDateWeekday`.
  return Math.round((end - start) / 86_400_000) + 1;
}

/** Every calendar date from `startDate` to `endDate`, both included. */
export function calendarDatesBetween(startDate: string, endDate: string): string[] {
  const count = calendarDayCount(startDate, endDate);
  if (!Number.isFinite(count) || count < 1) return [];
  const start = new Date(`${startDate}T12:00:00Z`);
  return Array.from({ length: count }, (_, i) => {
    const day = new Date(start.getTime() + i * 86_400_000);
    return day.toISOString().slice(0, 10);
  });
}

/** Why a run of days cannot be filled, or null when it can. */
export function validateBulkSlotRange(startDate: string, endDate: string): string | null {
  if (!isoDatePattern.test(startDate) || !isoDatePattern.test(endDate)) {
    return 'Please give both dates.';
  }
  if (endDate < startDate) return 'The last day cannot be before the first.';
  if (calendarDayCount(startDate, endDate) > bulkSlotMaxDays) {
    return `Please fill at most ${bulkSlotMaxDays} days at a time.`;
  }
  return null;
}

/**
 * Why a set of daily times cannot be used, or null when it can.
 *
 * Times that overlap each other are refused outright rather than quietly
 * de-duplicated: repeated across a month they are the same double-booking
 * `slotsOverlap` exists to prevent, and an admin who typed 09:00–11:00 under
 * 09:00–10:00 meant one of them.
 */
export function validateBulkSlotTimes(times: SlotTimeRangeType[]): string | null {
  if (times.length === 0) return 'Add at least one time of day.';
  for (const time of times) {
    if (!timePattern.test(time.startTime) || !timePattern.test(time.endTime)) {
      return 'Please give every time as HH:mm, 24-hour.';
    }
    if (time.endTime <= time.startTime) return 'Each end time must be after its start time.';
  }
  const sorted = [...times].sort((a, b) => a.startTime.localeCompare(b.startTime));
  if (sorted.some((time, i) => i > 0 && time.startTime < sorted[i - 1].endTime)) {
    return 'Those times overlap each other.';
  }
  return null;
}

/** What filling a run of days would actually write, and what it would leave out. */
export interface BulkSlotPlanType {
  /** The slots to create, in calendar order. */
  create: AvailabilitySlotDraftType[];
  /** Days passed over whole because the experience is closed on them. */
  closedDates: string[];
  /** Slots left out because one already covers that hour. */
  clashCount: number;
  /** Slots left out because they have already started. */
  pastCount: number;
  /** Days the range covers at all, closed ones included. */
  dayCount: number;
}

/**
 * Works out which slots a run of days would add, without adding any.
 *
 * Everything that cannot be created is skipped rather than refused, because the
 * useful case is exactly the messy one: filling next month over the top of a
 * fortnight already typed in should add the fortnight's worth that is missing,
 * not stop at the first day that clashes. What was skipped is counted so the
 * form can say so before the admin commits to it.
 *
 * Slots already under way are dropped for the same reason `isSlotBookable`
 * checks the clock: a run starting today would otherwise open this morning's
 * sittings, which nobody can take.
 */
export function planBulkSlots(
  startDate: string,
  endDate: string,
  times: SlotTimeRangeType[],
  existing: AvailabilitySlotDraftType[],
  closures: ExperienceClosuresType,
  now: string,
): BulkSlotPlanType {
  const dates = calendarDatesBetween(startDate, endDate);
  const create: AvailabilitySlotDraftType[] = [];
  const closedDates: string[] = [];
  let clashCount = 0;
  let pastCount = 0;

  // The calendar as it would stand, so times that overlap each other collide the
  // same way they would with a slot already stored.
  const taken = [...existing];
  const sortedTimes = [...times].sort((a, b) => a.startTime.localeCompare(b.startTime));

  for (const date of dates) {
    if (isDateClosed(date, closures)) {
      closedDates.push(date);
      continue;
    }
    for (const time of sortedTimes) {
      const draft: AvailabilitySlotDraftType = { date, ...time };
      if (slotStartInstant(draft) <= now) {
        pastCount++;
        continue;
      }
      if (taken.some((slot) => slotsOverlap(draft, slot))) {
        clashCount++;
        continue;
      }
      create.push(draft);
      taken.push(draft);
    }
  }

  return { create, closedDates, clashCount, pastCount, dayCount: dates.length };
}
